import React, { useState, useEffect, useCallback, useRef } from 'react';
import './image-carousel.scss';
import { SVGS } from '../assets';

interface ImageCarouselProps {
  images: string[];
  autoPlayInterval?: number;
}

export const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  autoPlayInterval = 5000,
}) => {
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const totalImages = images.length;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      goToNext();
    }, autoPlayInterval);
  }, [autoPlayInterval, clearTimer]);

  const goToNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev + 1) % totalImages);
    startTimer();
  }, [isAnimating, startTimer, totalImages]);

  const goToPrevious = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentSlide((prev) => (prev - 1 + totalImages) % totalImages);
    startTimer();
  }, [isAnimating, totalImages, startTimer]);

  const goToSlide = useCallback(
    (slideIndex: number) => {
      if (isAnimating || slideIndex === currentSlide) return;
      setIsAnimating(true);
      setCurrentSlide(slideIndex);
      startTimer();
    },
    [isAnimating, currentSlide, startTimer]
  );

  const handleTouchStart = (e) => {
    e.currentTarget.touchStartX = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const touchStartX = e.currentTarget.touchStartX;
    const touchEndX = e.changedTouches[0].clientX;
    const difference = touchStartX - touchEndX;
    if (Math.abs(difference) > 50) {
      if (difference > 0) {
        goToNext();
      } else {
        goToPrevious();
      }
    }
  };

  const handleMouseEnter = () => {
    clearTimer();
  };

  const handleMouseLeave = () => {
    startTimer();
  };

  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      return () => {};
    }
  }, [isAnimating]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrevious]);

  useEffect(() => {
    startTimer();
    return () => clearTimer();
  }, [startTimer, clearTimer]);

  return (
    <div
      className="image-carousel"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="carousel-slides">
        <div
          className="carousel-track"
          style={{
            transform: `translateX(-${currentSlide * 100}%)`,
            transition: isAnimating ? 'transform 0.5s ease' : 'none',
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {images.map((image, index) => (
            <div key={index} className="carousel-slide">
              <img src={image} alt={`slide-${index}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="carousel-controls">
        <img
          src={SVGS.ArrowLeft}
          alt=""
          onClick={goToPrevious}
          className="carousel-control"
        />
        <div className="indicators">
          {images.map((_, index) => (
            <div
              key={index}
              onClick={() => goToSlide(index)}
              className={`indicator ${index === currentSlide ? 'active' : ''}`}
            />
          ))}
        </div>
        <img
          src={SVGS.ArrowLeft}
          alt=""
          onClick={goToNext}
          className="carousel-control right"
        />
      </div>
    </div>
  );
};
